/**
 * Pure helpers for the tree-view side of `JsonTool`. Tests live in
 * `tests/web/jsonTreeTool.test.ts`.
 *
 * The shape here is deliberately flat-ish (each node carries its own
 * `path`, `depth`, `parentId`) so the React tree component can render
 * with `flat-list + virtualisation` later without rewriting the data
 * model. We don't virtualise yet because Monaco already absorbs the
 * "very large" case and the tree view is for "the JSON I want to
 * understand", not "the JSON I want to scroll through".
 */

export type JsonValueType =
  | 'object'
  | 'array'
  | 'string'
  | 'number'
  | 'boolean'
  | 'null';

export interface JsonNode {
  /** Stable id, monotonic in pre-order traversal. Used as React key. */
  id: number;
  /** JSONPath-ish, always rooted at `$`. e.g. `$.a.b[0].c`. */
  path: string;
  /** Object key, array index (as string) or '' for the root. */
  key: string;
  type: JsonValueType;
  /** 0 for the root, 1 for direct children, … */
  depth: number;
  /** Primitive value (`null` for null, the literal for boolean / number / string). */
  value?: unknown;
  /** Number of children for object / array; undefined for primitives. */
  size?: number;
  children?: JsonNode[];
}

export interface JsonStats {
  /** Total number of object key/value pairs across the whole tree. */
  keys: number;
  /** Number of arrays in the tree. */
  arrays: number;
  /** Number of objects in the tree. */
  objects: number;
  /** Number of leaf primitives (string / number / boolean / null). */
  leaves: number;
  /** Maximum depth from root (root = 0). */
  maxDepth: number;
  /** UTF-8 byte length of the (canonicalised) input. */
  utf8Bytes: number;
}

/**
 * Build a tree from already-parsed JSON. Throws only if `value` is
 * `undefined` (which `JSON.parse` never returns) — every other shape
 * is representable.
 */
export function buildTree(value: unknown): JsonNode {
  let nextId = 0;
  const make = (
    val: unknown,
    key: string,
    parentPath: string,
    depth: number,
  ): JsonNode => {
    const id = nextId++;
    // JSON.stringify quotes the key for valid path output; for plain
    // identifier keys we drop the brackets to match the conventional
    // JSONPath aesthetic used by jq, json.cn, jsonpath-plus, etc.
    const path =
      key === ''
        ? '$'
        : Array.isArray(parentPath)
          ? '' // unreachable; satisfies TS
          : `${parentPath}${joinPathSegment(key)}`;

    if (val === null) {
      return { id, path, key, type: 'null', depth, value: null };
    }
    const t = typeof val;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      return { id, path, key, type: t as JsonValueType, depth, value: val };
    }
    if (Array.isArray(val)) {
      const children = val.map((child, i) =>
        make(child, String(i), path, depth + 1),
      );
      return {
        id,
        path,
        key,
        type: 'array',
        depth,
        size: children.length,
        children,
      };
    }
    if (t === 'object') {
      const obj = val as Record<string, unknown>;
      const childKeys = Object.keys(obj);
      const children = childKeys.map((ck) =>
        make(obj[ck], ck, path, depth + 1),
      );
      return {
        id,
        path,
        key,
        type: 'object',
        depth,
        size: children.length,
        children,
      };
    }
    // Functions / symbols / bigints / undefined never come out of
    // `JSON.parse`. Fall back to string so the tree never crashes.
    return { id, path, key, type: 'string', depth, value: String(val) };
  };

  return make(value, '', '$', 0);
}

/** Walk the tree and aggregate counts. Single pass, no allocations beyond the result. */
export function computeStats(root: JsonNode, raw: string): JsonStats {
  let keys = 0;
  let arrays = 0;
  let objects = 0;
  let leaves = 0;
  let maxDepth = 0;
  const visit = (n: JsonNode): void => {
    if (n.depth > maxDepth) maxDepth = n.depth;
    switch (n.type) {
      case 'object':
        objects++;
        keys += n.size ?? 0;
        n.children?.forEach(visit);
        break;
      case 'array':
        arrays++;
        n.children?.forEach(visit);
        break;
      default:
        leaves++;
    }
  };
  visit(root);
  return {
    keys,
    arrays,
    objects,
    leaves,
    maxDepth,
    utf8Bytes: utf8ByteLength(raw),
  };
}

export interface SearchHit {
  /** Matched node id. */
  id: number;
  /** Where in the node the match occurred — useful for the UI to render two highlight tones. */
  matched: 'key' | 'value' | 'both';
}

/**
 * Search the tree for nodes whose `key` (object pairs / array indices)
 * or stringified `value` (primitive leaves) contains `query` (case-
 * insensitive). Returns nodes in pre-order so the "next match" arrow
 * can step linearly.
 *
 * Empty `query` returns `[]` (UI never lights up "all 124 matches" by
 * accident).
 */
export function findInTree(root: JsonNode, query: string): SearchHit[] {
  const q = query.trim();
  if (q.length === 0) return [];
  const qLower = q.toLowerCase();

  const out: SearchHit[] = [];
  const visit = (n: JsonNode): void => {
    let matchedKey = false;
    let matchedValue = false;
    if (n.depth > 0 && n.key.toLowerCase().includes(qLower)) {
      matchedKey = true;
    }
    if (n.type === 'string' || n.type === 'number' || n.type === 'boolean') {
      const s = String(n.value).toLowerCase();
      if (s.includes(qLower)) matchedValue = true;
    } else if (n.type === 'null') {
      if ('null'.includes(qLower)) matchedValue = true;
    }
    if (matchedKey || matchedValue) {
      out.push({
        id: n.id,
        matched: matchedKey && matchedValue ? 'both' : matchedKey ? 'key' : 'value',
      });
    }
    n.children?.forEach(visit);
  };
  visit(root);
  return out;
}

/** Compute the set of node ids on the path from root → target node. Used to "expand to match". */
export function ancestorIds(root: JsonNode, targetId: number): Set<number> {
  const set = new Set<number>();
  const visit = (n: JsonNode, trail: number[]): boolean => {
    if (n.id === targetId) {
      for (const id of trail) set.add(id);
      return true;
    }
    if (!n.children) return false;
    trail.push(n.id);
    for (const c of n.children) {
      if (visit(c, trail)) return true;
    }
    trail.pop();
    return false;
  };
  visit(root, []);
  return set;
}

/**
 * Render the value held by a leaf node back to a JSON literal —
 * primitives go through the obvious mapping; objects / arrays go
 * through `JSON.stringify` (no indent) so "copy node value" can paste
 * a self-contained snippet.
 */
export function nodeToJson(node: JsonNode, indent: number = 0): string {
  if (node.type === 'string') return JSON.stringify(node.value);
  if (node.type === 'number') return String(node.value);
  if (node.type === 'boolean') return String(node.value);
  if (node.type === 'null') return 'null';
  // Reconstitute a value from the children for object / array.
  return JSON.stringify(reconstitute(node), null, indent || undefined);
}

function reconstitute(node: JsonNode): unknown {
  if (node.type === 'object') {
    const out: Record<string, unknown> = {};
    for (const c of node.children ?? []) out[c.key] = reconstitute(c);
    return out;
  }
  if (node.type === 'array') {
    return (node.children ?? []).map(reconstitute);
  }
  if (node.type === 'null') return null;
  return node.value;
}

/**
 * Return `<parent>.<key>` for object keys (when the key is a plain JS
 * identifier) or `<parent>['<key>']` otherwise. Array indices always
 * render as `<parent>[i]`.
 */
function joinPathSegment(key: string): string {
  // Numeric → array index segment.
  if (/^\d+$/.test(key)) return `[${key}]`;
  // Plain identifier → dot segment.
  if (/^[A-Za-z_$][\w$]*$/.test(key)) return `.${key}`;
  // Anything else → bracket-quoted segment.
  return `[${JSON.stringify(key)}]`;
}

function utf8ByteLength(s: string): number {
  // `TextEncoder` is the canonical way to get the byte length and is
  // available everywhere we run (including the vitest node env).
  return new TextEncoder().encode(s).byteLength;
}
